import React, { useEffect, useState } from "react";
import { TfiClose } from "react-icons/tfi";
import { Link, useLocation } from "react-router-dom";
import { FaBars } from "react-icons/fa";
import { animateScroll as scroll } from "react-scroll";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { motion, AnimatePresence } from "framer-motion";
import { TRAINING_NAV_ITEMS } from "../data/trainingData";

const navLinks = [
  { text: "Home", url: "/" },
  {
    text: "Services",
    url: "/services",
    submenu: true,
    /* Each item now opens its OWN page (see ServiceCategoryPage) instead of a
       hash on one shared listing, so a page only ever shows its own service.
       Routes are generated from the same CATEGORY_ORDER these names come from.
       Desktop and mobile render from this one array. */
    dropdown: [
      { name: "Quality", link: "/services/quality" },
      { name: "Food Safety", link: "/services/food-safety" },
      { name: "Health and Security", link: "/services/health-and-security" },
      { name: "Business Excellence", link: "/services/business-excellence" },
      { name: "Product / Process Certification", link: "/services/product-process-certification" },
      /* Cloud Security and Cyber Security were folded into Information
         Security on 2026-09-14 — one item, 35 programs. Their old routes
         redirect (see App.js) rather than 404. */
      { name: "Information Security", link: "/services/information-security" },
    ],
  },
  {
    text: "About Us",
    url: "/about",
    submenu: true,
    /* Ordered as the company-information journey a visitor actually follows:
       who we are -> who you work with -> what we are accredited for -> who we
       do it for -> proof -> join us. The old "About MBA Solutions" label is
       gone; the same page is here as the plain, descriptive "About Us". Every
       link points at the page's existing route — no route was duplicated to
       suit the menu. */
    dropdown: [
      { name: "Our Team", link: "/our-team" },
      { name: "Certifications", link: "/Certifications" },
      { name: "Industries We Serve", link: "/industries-we-serve" },
      { name: "Clientele", link: "/clientele" },
      { name: "Jobs", link: "/jobs" },
    ],
  },
  {
    text: "Training",
    url: "/training",
    submenu: true,
    courseMenu: true,
    dropdown: TRAINING_NAV_ITEMS,
  },
  {
    text: "Resources",
    url: "/blog",
    submenu: true,
    dropdown: [
      { name: "Blog", link: "/blog" },
      { name: "Downloads", link: "/downloads" },
      { name: "FAQs", link: "/faq" },
    ],
  },
  { text: "Tracking", url: "/tracking" },
];

const dropdownVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const isNavActive = (pathname, url) => {
  if (url === "/") return pathname === "/";
  if (url === "/services") {
    return pathname === "/services" || pathname.startsWith("/service/");
  }
  if (url === "/training") {
    return (
      pathname === "/training" ||
      pathname.startsWith("/highfield-training") ||
      pathname.startsWith("/haccp-training") ||
      pathname.startsWith("/pecb-training") ||
      pathname.startsWith("/corporate-training") ||
      pathname.startsWith("/iosh-managing-safely-training") ||
      pathname.startsWith("/safety-training") ||
      pathname.startsWith("/iso-lead-auditor-training") ||
      pathname.startsWith("/training-") ||
      pathname.startsWith("/pecb/")
    );
  }
  return pathname === url || pathname.startsWith(`${url}/`);
};

const contactLinkClassName =
  "inline-block px-6 py-2.5 rounded-full bg-[#F86624] hover:bg-[#D9531A] text-white font-medium shadow-lg relative overflow-hidden group hidden lg:block hover:text-white transition-transform";

const mobileContactLinkClassName =
  "mt-6 block w-full py-3 rounded-full bg-[#F86624] hover:bg-[#D9531A] text-white font-medium shadow-lg text-center hover:text-white";

const Navbar = () => {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeNestedMobile, setActiveNestedMobile] = useState(null);

  const Logo = `${process.env.PUBLIC_URL}/assets/Logo.webp`;

  useEffect(() => {
    setNavOpen(false);
    setActiveSubmenu(null);
    setHoveredIndex(null);
    setActiveNestedMobile(null);
  }, [location.pathname, location.hash]);

  const handleNavToggle = () => setNavOpen((open) => !open);

  const handleLinkClick = (link) => {
    if (!link?.includes("#")) {
      scroll.scrollToTop();
    }
    setNavOpen(false);
  };

  const isSubmenuOpen = (index) =>
    navOpen ? activeSubmenu === index : hoveredIndex === index;

  const renderNavLink = (subItem, className = "") => (
    <Link
      role="menuitem"
      to={subItem.link}
      onClick={() => handleLinkClick(subItem.link)}
      aria-current={
        isNavActive(location.pathname, subItem.link.split("#")[0])
          ? "page"
          : undefined
      }
      className={`text-gray-700 hover:text-[#F86624] text-sm transition-colors block ${className}`}
    >
      {subItem.name}
    </Link>
  );

  const renderCourseMenuItem = (subItem) => {
    const hasChildren = Array.isArray(subItem.children) && subItem.children.length > 0;

    if (!hasChildren) {
      return (
        <li key={`${subItem.link}-${subItem.name}`} role="none">
          {renderNavLink(
            subItem,
            "px-4 py-2.5 hover:bg-gray-50 whitespace-nowrap"
          )}
        </li>
      );
    }

    return (
      <li
        key={`${subItem.link}-${subItem.name}`}
        role="none"
        className="relative group/flyout"
      >
        <Link
          role="menuitem"
          to={subItem.link}
          onClick={() => handleLinkClick(subItem.link)}
          aria-haspopup="true"
          className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-gray-700 hover:text-[#F86624] hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
          <span>{subItem.name}</span>
          <BsChevronDown
            size={12}
            className="-rotate-90 text-gray-400 group-hover/flyout:text-[#F86624]"
            aria-hidden="true"
          />
        </Link>
        <ul
          role="menu"
          className="absolute left-full top-0 ml-0 min-w-[280px] max-w-[340px] bg-white border border-gray-100 shadow-lg rounded-md py-2 list-none opacity-0 invisible pointer-events-none group-hover/flyout:opacity-100 group-hover/flyout:visible group-hover/flyout:pointer-events-auto transition-all z-[1100]"
        >
          {subItem.children.map((child) => (
            <li key={`${child.link}-${child.name}`} role="none">
              {renderNavLink(
                child,
                "px-4 py-2.5 hover:bg-gray-50 whitespace-normal leading-snug"
              )}
            </li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <header className="poppins sticky top-0 z-[1000] bg-white h-24 shadow-sm">
      <div className="h-24 flex justify-between items-center md:px-7 p-4 text-sm">
        <div className="flex items-center">
          <Link to="/" onClick={() => handleLinkClick("/")} aria-label="MBA Solutions home">
            <img
              src={Logo}
              alt="MBA Solutions - ISO Certification Services"
              className="h-16"
            />
          </Link>
        </div>

        <nav
          aria-label="Main navigation"
          className="hidden lg:flex items-center space-x-6 font-medium text-gray-800"
        >
          <ul className="flex items-center space-x-6">
            {navLinks.map((item, index) => {
              const submenuId = `desktop-submenu-${index}`;
              const active = isNavActive(location.pathname, item.url);

              return (
                <li
                  key={item.text}
                  className="relative list-none"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Link
                    to={item.url}
                    onClick={() => handleLinkClick(item.url)}
                    aria-current={active ? "page" : undefined}
                    aria-haspopup={item.submenu ? "true" : undefined}
                    aria-expanded={item.submenu ? isSubmenuOpen(index) : undefined}
                    aria-controls={item.submenu ? submenuId : undefined}
                    className="flex items-center gap-1 hover:text-[#F86624] transition-colors"
                  >
                    {item.text}
                    {item.submenu && (
                      <BsChevronDown size={14} aria-hidden="true" />
                    )}
                  </Link>

                  {item.submenu && (
                    <motion.div
                      id={submenuId}
                      role="menu"
                      initial="hidden"
                      animate={hoveredIndex === index ? "visible" : "hidden"}
                      variants={dropdownVariants}
                      className={`absolute top-8 left-0 bg-white rounded-md shadow-lg border border-gray-100 transition-all ${
                        item.courseMenu ? "w-[280px]" : "w-48"
                      } ${
                        hoveredIndex === index
                          ? "opacity-100 visible"
                          : "opacity-0 invisible"
                      }`}
                    >
                      <ul
                        className={`py-2 flex flex-col list-none ${
                          item.courseMenu
                            ? "max-h-[75vh] overflow-y-auto"
                            : "px-1 space-y-1 max-h-60 overflow-y-auto"
                        }`}
                      >
                        {item.courseMenu
                          ? item.dropdown.map((subItem) =>
                              renderCourseMenuItem(subItem)
                            )
                          : item.dropdown.map((subItem) => (
                              <li
                                key={`${subItem.link}-${subItem.name}`}
                                role="none"
                              >
                                {renderNavLink(
                                  subItem,
                                  "px-3 py-2 hover:bg-gray-50"
                                )}
                              </li>
                            ))}
                      </ul>
                    </motion.div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Link
            to="/contact"
            onClick={() => handleLinkClick("/contact")}
            className={contactLinkClassName}
            aria-current={isNavActive(location.pathname, "/contact") ? "page" : undefined}
          >
            <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="relative z-10">Contact Us</span>
          </Link>
        </motion.div>

        <div className="lg:hidden">
          <button
            type="button"
            onClick={handleNavToggle}
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            aria-controls="mobile-navigation"
          >
            <FaBars size={22} className="text-gray-700" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.nav
            id="mobile-navigation"
            aria-label="Mobile navigation"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed top-0 right-0 w-80 h-screen bg-white shadow-2xl z-[2000] p-6 overflow-y-auto lg:hidden"
          >
            <div className="flex justify-end mb-6">
              <button
                type="button"
                onClick={handleNavToggle}
                aria-label="Close menu"
              >
                <TfiClose size={22} className="cursor-pointer" />
              </button>
            </div>

            <ul className="space-y-4 list-none">
              {navLinks.map((item, index) => {
                const submenuId = `mobile-submenu-${index}`;

                return (
                  <li key={item.text}>
                    <div className="flex justify-between items-center text-gray-800 font-medium">
                      <Link
                        to={item.url}
                        onClick={() => handleLinkClick(item.url)}
                        aria-current={
                          isNavActive(location.pathname, item.url)
                            ? "page"
                            : undefined
                        }
                        className="hover:text-[#F86624]"
                      >
                        {item.text}
                      </Link>
                      {item.submenu && (
                        <button
                          type="button"
                          aria-label={`Toggle ${item.text} submenu`}
                          aria-expanded={activeSubmenu === index}
                          aria-controls={submenuId}
                          onClick={() =>
                            setActiveSubmenu(
                              activeSubmenu === index ? null : index
                            )
                          }
                        >
                          <BsChevronUp
                            className={`transition-transform ${
                              activeSubmenu === index ? "rotate-180" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>

                    {item.submenu && activeSubmenu === index && (
                      <ul
                        id={submenuId}
                        className="ml-3 mt-2 space-y-1 list-none border-l border-gray-100 pl-3"
                      >
                        {item.courseMenu
                          ? item.dropdown.map((subItem) => {
                              const hasChildren =
                                Array.isArray(subItem.children) &&
                                subItem.children.length > 0;
                              const nestedKey = `${item.text}-${subItem.name}`;

                              return (
                                <li key={`${subItem.link}-${subItem.name}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <Link
                                      to={subItem.link}
                                      onClick={() =>
                                        handleLinkClick(subItem.link)
                                      }
                                      className="block text-sm text-gray-600 hover:text-[#F86624] py-1.5 flex-1"
                                    >
                                      {subItem.name}
                                    </Link>
                                    {hasChildren && (
                                      <button
                                        type="button"
                                        aria-label={`Toggle ${subItem.name} submenu`}
                                        aria-expanded={
                                          activeNestedMobile === nestedKey
                                        }
                                        onClick={() =>
                                          setActiveNestedMobile(
                                            activeNestedMobile === nestedKey
                                              ? null
                                              : nestedKey
                                          )
                                        }
                                        className="p-1 text-gray-500"
                                      >
                                        <BsChevronDown
                                          size={12}
                                          className={`transition-transform ${
                                            activeNestedMobile === nestedKey
                                              ? "rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </button>
                                    )}
                                  </div>
                                  {hasChildren &&
                                    activeNestedMobile === nestedKey && (
                                      <ul className="ml-2 mt-1 mb-2 space-y-1 list-none border-l border-gray-100 pl-3">
                                        {subItem.children.map((child) => (
                                          <li
                                            key={`${child.link}-${child.name}`}
                                          >
                                            <Link
                                              to={child.link}
                                              onClick={() =>
                                                handleLinkClick(child.link)
                                              }
                                              className="block text-sm text-gray-500 hover:text-[#F86624] py-1 leading-snug"
                                            >
                                              {child.name}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                </li>
                              );
                            })
                          : item.dropdown.map((subItem) => (
                              <li key={`${subItem.link}-${subItem.name}`}>
                                <Link
                                  to={subItem.link}
                                  onClick={() => handleLinkClick(subItem.link)}
                                  aria-current={
                                    isNavActive(
                                      location.pathname,
                                      subItem.link.split("#")[0]
                                    )
                                      ? "page"
                                      : undefined
                                  }
                                  className="block text-sm text-gray-600 hover:text-[#F86624] py-1.5"
                                >
                                  {subItem.name}
                                </Link>
                              </li>
                            ))}
                      </ul>
                    )}
                  </li>
                );
              })}

              <li>
                <Link
                  to="/contact"
                  onClick={() => handleLinkClick("/contact")}
                  className={mobileContactLinkClassName}
                  aria-current={
                    isNavActive(location.pathname, "/contact") ? "page" : undefined
                  }
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
